const DAY_MS = 86_400_000;

/** Whole days from `iso` to `now`; null when missing or unparseable, never negative. */
export function daysSince(iso: string | null | undefined, now: Date): number | null {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  return Math.max(0, Math.floor((now.getTime() - then) / DAY_MS));
}

/** A user with no activity row, or no last-active time, counts as inactive. */
export function isInactive(
  activity: { last_active_utc: string | null } | undefined,
  now: Date,
  days = 90,
): boolean {
  const since = daysSince(activity?.last_active_utc, now);
  return since === null || since >= days;
}

/** The identity key used by the activity tables: SP -> client_id, user -> entra_object_id. */
export function identityIdFor(user: {
  is_service_principal?: string;
  client_id?: string | null;
  entra_object_id?: string | null;
}): string | undefined {
  const id =
    user.is_service_principal === "Y"
      ? user.client_id || user.entra_object_id
      : user.entra_object_id;
  return id || undefined;
}

/**
 * Enabled users that are inactive. `activitiesByIdentity` is keyed by lowercase
 * identity id; a user with no row counts as inactive.
 */
export function filterInactiveUsers<
  U extends {
    enabled?: string | null;
    is_service_principal?: string;
    client_id?: string | null;
    entra_object_id?: string | null;
  },
>(
  users: U[],
  activitiesByIdentity: Map<string | undefined, { last_active_utc: string | null }>,
  now: Date,
  days = 90,
): U[] {
  return users.filter((user) => {
    if (user.enabled !== "Y") return false;
    const id = identityIdFor(user);
    return isInactive(id ? activitiesByIdentity.get(id.toLowerCase()) : undefined, now, days);
  });
}

/** The Refresh access button needs write rights, an Entra id and a handler. */
export function canRefreshAccess(opts: {
  canDelete?: boolean;
  entraObjectId?: string | null;
  hasHandler: boolean;
}): boolean {
  return Boolean(opts.canDelete && opts.entraObjectId && opts.hasHandler);
}

/**
 * The activity route returns one row per identity per domain. Merge them to one
 * row per identity (case-insensitive), keeping the newest last_active_utc.
 */
export function mergeIdentityActivities<
  T extends { identity_id?: string | null; last_active_utc: string | null },
>(rows: T[]): T[] {
  const byId = new Map<string, T>();
  rows.forEach((row) => {
    if (!row.identity_id) return;
    const key = row.identity_id.toLowerCase();
    const current = byId.get(key);
    const rowTime = row.last_active_utc ? new Date(row.last_active_utc).getTime() : -Infinity;
    const curTime = current?.last_active_utc ? new Date(current.last_active_utc).getTime() : -Infinity;
    if (!current || rowTime > curTime) byId.set(key, row);
  });
  return Array.from(byId.values());
}

/** Read every page of an offset/limit list route. Rejects if any page fails. */
export async function fetchAllPages<T>(
  fetchPage: (offset: number, limit: number) => Promise<{ items: T[]; total: number }>,
  pageSize = 500,
): Promise<T[]> {
  const all: T[] = [];
  for (let offset = 0; ; offset += pageSize) {
    const { items, total } = await fetchPage(offset, pageSize);
    all.push(...items);
    if (items.length === 0 || items.length < pageSize || all.length >= total) break;
  }
  return all;
}

/** Label and disabled state for the Inactive toggle. No count when activity failed to load. */
export function inactiveToggleLabel(
  available: boolean,
  count: number,
): { disabled: boolean; text: string } {
  return available
    ? { disabled: false, text: `Inactive 90+ days (${count})` }
    : { disabled: true, text: "Activity unavailable" };
}
